import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MapIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Layout, LayoutFilters, PaginatedResponse } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';

const LayoutsPage: React.FC = () => {
  const [layouts, setLayouts] = useState<Layout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<LayoutFilters>({
    page: 1,
    limit: 10,
    search: '',
    city: '',
    state: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; layout: Layout | null }>({
    show: false,
    layout: null
  });
  const [searchTerm, setSearchTerm] = useState('');

  const { state: authState } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadLayouts();
  }, [filters]);

  const loadLayouts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<Layout> = await apiService.getLayouts(filters);
      setLayouts(response.data.items);
      setPagination({
        ...response.data.pagination,
        total: response.data.pagination.total || 0
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load layouts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      search: searchTerm,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof LayoutFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDeleteLayout = async (layout: Layout) => {
    if (!window.confirm(`Are you sure you want to delete layout "${layout.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiService.deleteLayout(layout.id);
      setDeleteModal({ show: false, layout: null });
      loadLayouts(); // Reload the list
    } catch (err: any) {
      alert(err.message || 'Failed to delete layout');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const isAdmin = authState.user?.role === 'Admin';

  if (loading && layouts.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-48"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        
        <div className="card p-6">
          <div className="animate-pulse space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex space-x-4">
                <div className="h-4 bg-gray-200 rounded flex-1"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Layouts</h1>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">Manage your plot layouts</p>
        </div>
        {isAdmin && (
          <Link to="/layouts/new" className="btn-primary inline-flex items-center justify-center">
            <PlusIcon className="h-5 w-5 mr-2" />
            <span className="hidden sm:inline">Create Layout</span>
            <span className="sm:hidden">Create</span>
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <div className="card p-4 sm:p-6">
        <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4 md:space-y-0 md:flex md:items-end md:space-x-4">
          <div className="flex-1">
            <label className="form-label">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="form-input pl-10"
                placeholder="Search by name, location, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="md:w-48">
            <label className="form-label">City</label>
            <select
              className="form-input"
              value={filters.city || ''}
              onChange={(e) => handleFilterChange('city', e.target.value)}
            >
              <option value="">All Cities</option>
              <option value="Mumbai">Mumbai</option>
              <option value="Pune">Pune</option>
              <option value="Bangalore">Bangalore</option>
              <option value="Hyderabad">Hyderabad</option>
              <option value="Chennai">Chennai</option>
            </select>
          </div>
          
          <div className="md:w-48">
            <label className="form-label">State</label>
            <select
              className="form-input"
              value={filters.state || ''}
              onChange={(e) => handleFilterChange('state', e.target.value)}
            >
              <option value="">All States</option>
              <option value="Maharashtra">Maharashtra</option>
              <option value="Karnataka">Karnataka</option>
              <option value="Telangana">Telangana</option>
              <option value="Tamil Nadu">Tamil Nadu</option>
            </select>
          </div>
          
          <button type="submit" className="btn-primary">
            Search
          </button>
          
          {(filters.search || filters.city || filters.state) && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setFilters({ page: 1, limit: 10, search: '', city: '', state: '' });
              }}
              className="btn-outline"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Error State */}
      {error && (
        <div className="card p-6">
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadLayouts} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {layouts.length === 0 ? (
          <div className="card p-6 text-center">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">
              {filters.search || filters.city || filters.state
                ? 'No layouts found'
                : 'No layouts yet'
              }
            </p>
            {isAdmin && !filters.search && !filters.city && !filters.state && (
              <Link to="/layouts/new" className="btn-primary mt-4 inline-flex items-center">
                <PlusIcon className="h-4 w-4 mr-2" />
                Create First Layout
              </Link>
            )}
          </div>
        ) : (
          layouts.map((layout) => (
            <div key={layout.id} className="card p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <MapIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{layout.name}</h3>
                    <p className="text-xs text-gray-500 truncate">{layout.location}</p>
                    <p className="text-xs text-gray-500">{layout.city}, {layout.state}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <Link
                    to={`/layouts/${layout.id}`}
                    className="text-indigo-600 hover:text-indigo-900 p-1"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </Link>
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => navigate(`/layouts/${layout.id}/edit`)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteLayout(layout)}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-gray-500">Plots:</span>
                  <span className="ml-1 font-medium text-gray-900">{layout.total_plots}</span>
                </div>
                <div>
                  <span className="text-gray-500">Created:</span>
                  <span className="ml-1 text-gray-900">{formatDate(layout.created_at)}</span>
                </div>
                <div className="truncate">
                  <span className="text-gray-500">By:</span>
                  <span className="ml-1 text-gray-900">{layout.created_by_name || 'System'}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Layout
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Plots
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {layouts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {filters.search || filters.city || filters.state
                        ? 'No layouts found matching your search criteria'
                        : 'No layouts created yet'
                      }
                    </p>
                    {isAdmin && !filters.search && !filters.city && !filters.state && (
                      <Link to="/layouts/new" className="btn-primary mt-4 inline-flex">
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Create First Layout
                      </Link>
                    )}
                  </td>
                </tr>
              ) : (
                layouts.map((layout) => (
                  <tr key={layout.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <MapIcon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {layout.name}
                          </div>
                          {layout.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {layout.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{layout.location}</div>
                      <div className="text-sm text-gray-500">
                        {layout.city}, {layout.state}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {layout.total_plots}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(layout.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {layout.created_by_name || 'System'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/layouts/${layout.id}`}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Layout"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => navigate(`/layouts/${layout.id}/edit`)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit Layout"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLayout(layout)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Layout"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination - Appears after both mobile and desktop views */}
      {layouts.length > 0 && (
        <div className="card px-4 py-3 flex items-center justify-between">
          <div className="flex-1 flex justify-between md:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-gray-600 flex items-center">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden md:flex-1 md:flex md:items-center md:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {(pagination.currentPage - 1) * filters.limit! + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * filters.limit!, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = i + 1;
                  const isActive = page === pagination.currentPage;
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        isActive
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && layouts.length > 0 && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <div className="flex items-center">
              <div className="loading-spinner mr-3"></div>
              <p>Loading layouts...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LayoutsPage; 